import { getPlatformApiBaseUrl } from '../../api/client.ts';
import { localizeApiError } from '../../api/errorMessages.ts';
import { useToast } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import { StatusLamp, Table, TBody, Td, THead, Th, Tr } from '../../ui/index.ts';
import { FactCard } from '../docs/EndpointCards.tsx';
import {
  ActionLink,
  DocsCallout,
  DocsSection,
  SnippetCard,
  tocChipClass,
  tocItemClass,
} from '../docs/primitives.tsx';
import { useActiveSection } from '../docs/useActiveSection.ts';
import type { I18nKey } from '../../i18n/index.ts';
import type { AdminEndpoint, AdminField, AdminGroup, AdminMethod } from './reference.ts';
import { ADMIN_ENDPOINTS, ADMIN_ERROR_ENVELOPE, ADMIN_GROUPS, adminApiCurl } from './reference.ts';

/**
 * Bilingual reference for `GET/PATCH /v1/admin/*`.
 *
 * The base URL is the same platform origin the console uses for its own
 * API calls, so a staging bundle documents staging. Samples stay
 * language-neutral and use `$NXTPATH_ADMIN_KEY`.
 */

const GROUP_TITLE: Record<AdminGroup, I18nKey> = {
  key: 'adminApi.doc.group.key',
  members: 'adminApi.doc.group.members',
  usage: 'adminApi.doc.group.usage',
  billing: 'adminApi.doc.group.billing',
};

/** Read verbs are green, write verbs rust, so a write never passes as a read. */
const METHOD_STYLE: Record<AdminMethod, { badge: string; edge: string }> = {
  GET: { badge: 'border-ok-line bg-ok-tint text-forest', edge: 'border-l-moss' },
  PATCH: { badge: 'border-rust-line bg-rust-tint text-rust-text', edge: 'border-l-rust' },
};

function groupAnchor(group: AdminGroup): string {
  return `admin-api-group-${group}`;
}

/** Left-rail sections, in page order (same rail as /docs). */
const TOC: readonly { readonly id: string; readonly titleKey: I18nKey }[] = [
  { id: 'admin-api-auth', titleKey: 'adminApi.doc.auth.title' },
  { id: 'admin-api-conventions', titleKey: 'adminApi.doc.conventions.title' },
  { id: 'admin-api-endpoints', titleKey: 'adminApi.doc.endpoints.title' },
  ...ADMIN_GROUPS.map((group) => ({ id: groupAnchor(group), titleKey: GROUP_TITLE[group] })),
];
const TOC_IDS = TOC.map((item) => item.id);

function endpointAnchor(endpoint: AdminEndpoint): string {
  return `admin-api-ep-${endpoint.id}`;
}

function MethodBadge({ method }: { readonly method: AdminMethod }): JSX.Element {
  return (
    <span
      data-testid="admin-api-method"
      data-method={method}
      className={`inline-flex min-w-[3.75rem] justify-center rounded border px-1.5 py-0.5 font-mono text-label font-semibold ${METHOD_STYLE[method].badge}`}
    >
      {method}
    </span>
  );
}

const AUTH_HEADER = 'Authorization: Bearer $NXTPATH_ADMIN_KEY';

function adminApiBaseUrl(): string {
  const origin = getPlatformApiBaseUrl().replace(/\/$/, '');
  return `${origin}/v1/admin`;
}

export function ApiReference(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const base = adminApiBaseUrl();
  const activeSection = useActiveSection(TOC_IDS);
  const tocItems = TOC.map((item) => ({ id: item.id, label: t(item.titleKey) }));

  async function copy(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('docs.toast.copied'));
    } catch (err) {
      toast.error(localizeApiError(err, t));
    }
  }

  return (
    <div className="flex flex-col gap-4" data-testid="admin-api-reference">
      <p className="m-0 text-body leading-relaxed text-ink-mid">
        {t('adminApi.doc.overview.body')}
      </p>
      {/* Same opening strip as /docs: where to call, how to sign, how often. */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3" data-testid="admin-api-fact-cards">
        <FactCard
          title={t('adminApi.doc.fact.base.title')}
          note={t('adminApi.doc.fact.base.note')}
          noteTone="accent"
          value={base}
          copyLabel={t('adminApi.doc.base.copy')}
          onCopy={() => {
            void copy(base);
          }}
          testId="admin-api-base-url"
        />
        <FactCard
          title={t('adminApi.doc.fact.auth.title')}
          note={t('adminApi.doc.fact.auth.note')}
          noteTone="warn"
          value={AUTH_HEADER}
          copyLabel={t('adminApi.doc.base.copy')}
          onCopy={() => {
            void copy(AUTH_HEADER);
          }}
          testId="admin-api-auth-header"
        />
        <FactCard
          title={t('adminApi.doc.fact.rate.title')}
          note={t('adminApi.doc.fact.rate.note')}
          value={t('adminApi.doc.fact.rate.value')}
          testId="admin-api-rate-limit"
        />
      </div>

      <nav
        aria-label={t('adminApi.doc.toc.aria')}
        data-testid="admin-api-toc-chips"
        className="flex flex-wrap gap-1.5 xl:hidden"
      >
        {tocItems.map((item) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            aria-current={item.id === activeSection ? 'true' : undefined}
            className={tocChipClass(item.id === activeSection)}
          >
            {item.label}
          </a>
        ))}
      </nav>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[11rem_minmax(0,1fr)]">
        <aside className="hidden xl:block">
          <nav
            aria-label={t('adminApi.doc.toc.aria')}
            data-testid="admin-api-toc"
            className="sticky top-4 flex flex-col gap-0.5 rounded-lg border border-line-soft bg-card p-1.5"
          >
            {tocItems.map((item) => {
              const isActive = item.id === activeSection;
              return (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  aria-current={isActive ? 'true' : undefined}
                  data-testid={`admin-api-toc-item-${item.id}`}
                  className={tocItemClass(isActive)}
                >
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {isActive ? <StatusLamp tone="rust" size="sm" /> : null}
                </a>
              );
            })}
          </nav>
        </aside>

        <div className="flex min-w-0 flex-col gap-4">
          <DocsSection id="admin-api-auth" title={t('adminApi.doc.auth.title')}>
            <p className="m-0 text-body leading-relaxed text-ink-mid">
              {t('adminApi.doc.auth.body')}
            </p>
            <p className="m-0 text-body leading-relaxed text-ink-mid">
              {t('adminApi.doc.auth.lifecycle')}
            </p>
            <h3 className="m-0 text-body font-medium text-ink">
              {t('adminApi.doc.auth.errors_title')}
            </h3>
            <ul className="m-0 flex list-disc flex-col gap-2 pl-5 text-body leading-relaxed text-ink-mid">
              <li>{t('adminApi.doc.auth.err.required')}</li>
              <li>{t('adminApi.doc.auth.err.invalid')}</li>
              <li>{t('adminApi.doc.auth.err.inactive')}</li>
              <li>{t('adminApi.doc.auth.err.rate')}</li>
            </ul>
          </DocsSection>

          <DocsSection id="admin-api-conventions" title={t('adminApi.doc.conventions.title')}>
            <p className="m-0 text-body leading-relaxed text-ink-mid">
              {t('adminApi.doc.conventions.snake')}
            </p>
            <p className="m-0 text-body leading-relaxed text-ink-mid">
              {t('adminApi.doc.conventions.money')}
            </p>
            <p className="m-0 text-body leading-relaxed text-ink-mid">
              {t('adminApi.doc.conventions.time')}
            </p>
            <p className="m-0 text-body leading-relaxed text-ink-mid">
              {t('adminApi.doc.conventions.page')}
            </p>
            <p className="m-0 text-body leading-relaxed text-ink-mid">
              {t('adminApi.doc.conventions.window')}
            </p>
            <p className="m-0 text-body leading-relaxed text-ink-mid">
              {t('adminApi.doc.conventions.error')}
            </p>
            <SnippetCard
              title={t('adminApi.doc.conventions.error_shape')}
              caption={null}
              code={ADMIN_ERROR_ENVELOPE}
              copyLabel={t('adminApi.doc.base.copy')}
              onCopy={() => {
                void copy(ADMIN_ERROR_ENVELOPE);
              }}
              testId="admin-api-error-envelope"
            />
          </DocsSection>

          <DocsSection id="admin-api-endpoints" title={t('adminApi.doc.endpoints.title')}>
            <EndpointIndex />
          </DocsSection>

          {ADMIN_GROUPS.map((group) => (
            <DocsSection key={group} id={groupAnchor(group)} title={t(GROUP_TITLE[group])}>
              {ADMIN_ENDPOINTS.filter((endpoint) => endpoint.group === group).map((endpoint) => (
                <EndpointCard
                  key={endpoint.id}
                  endpoint={endpoint}
                  base={base}
                  onCopy={(text) => {
                    void copy(text);
                  }}
                />
              ))}
            </DocsSection>
          ))}
        </div>
      </div>

      <DocsSection id="admin-api-openapi" title={t('adminApi.doc.openapi.title')}>
        <p className="m-0 text-body leading-relaxed text-ink-mid">
          {t('adminApi.doc.openapi.body')}
        </p>
        <ActionLink href={`${base}/openapi.json`} label={t('adminApi.doc.openapi.link')} external />
      </DocsSection>
    </div>
  );
}

/** One line per endpoint: what it does, then the call. Links to the card. */
function EndpointIndex(): JSX.Element {
  const t = useT();
  return (
    <nav
      aria-label={t('adminApi.doc.index.label')}
      data-testid="admin-api-index"
      className="grid gap-4 rounded-lg border border-line-soft bg-card p-4 sm:grid-cols-2"
    >
      {ADMIN_GROUPS.map((group) => (
        <div key={group} className="flex flex-col gap-2">
          <h4 className="m-0 text-label font-medium text-ink-low">{t(GROUP_TITLE[group])}</h4>
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {ADMIN_ENDPOINTS.filter((endpoint) => endpoint.group === group).map((endpoint) => (
              <li key={endpoint.id}>
                <a
                  href={`#${endpointAnchor(endpoint)}`}
                  className="flex flex-wrap items-center gap-2 text-body text-ink no-underline hover:text-forest"
                >
                  <MethodBadge method={endpoint.method} />
                  <span className="font-medium">{t(endpoint.titleKey)}</span>
                  <span className="font-mono text-label text-ink-low">{endpoint.path}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function EndpointCard({
  endpoint,
  base,
  onCopy,
}: {
  readonly endpoint: AdminEndpoint;
  readonly base: string;
  readonly onCopy: (text: string) => void;
}): JSX.Element {
  const t = useT();
  const curl = adminApiCurl(base, endpoint);
  return (
    <article
      id={endpointAnchor(endpoint)}
      data-testid={`admin-api-endpoint-${endpoint.id}`}
      className={`flex scroll-mt-4 flex-col gap-3 rounded-lg border border-l-4 border-line-soft bg-well p-4 ${METHOD_STYLE[endpoint.method].edge}`}
    >
      <div className="flex flex-col gap-1.5">
        <h3 className="m-0 text-body font-semibold text-ink">{t(endpoint.titleKey)}</h3>
        <div className="flex flex-wrap items-center gap-2">
          <MethodBadge method={endpoint.method} />
          <code className="font-mono text-body text-ink">{endpoint.path}</code>
        </div>
      </div>
      <p className="m-0 text-body leading-relaxed text-ink-mid">{t(endpoint.summaryKey)}</p>
      {endpoint.highlightKey !== undefined ? (
        <DocsCallout tone="warning">{t(endpoint.highlightKey)}</DocsCallout>
      ) : null}
      <FieldTable caption={t('adminApi.doc.params.query')} rows={endpoint.query} />
      <FieldTable caption={t('adminApi.doc.params.body')} rows={endpoint.body} />
      <FieldTable caption={t('adminApi.doc.params.response')} rows={endpoint.response} />
      {endpoint.noteKey !== undefined ? (
        <p className="m-0 text-body leading-relaxed text-ink-mid">{t(endpoint.noteKey)}</p>
      ) : null}
      <SnippetCard
        title={t('adminApi.doc.curl')}
        caption={null}
        code={curl}
        copyLabel={t('adminApi.doc.base.copy')}
        onCopy={() => onCopy(curl)}
        testId={`admin-api-curl-${endpoint.id}`}
      />
    </article>
  );
}

function FieldTable({
  caption,
  rows,
}: {
  readonly caption: string;
  readonly rows: readonly AdminField[];
}): JSX.Element {
  const t = useT();
  if (rows.length === 0) {
    return (
      <div>
        <h4 className="m-0 mb-1 text-label font-medium text-ink">{caption}</h4>
        <p className="m-0 text-body text-ink-low">{t('adminApi.doc.params.none')}</p>
      </div>
    );
  }
  return (
    <div>
      <h4 className="m-0 mb-2 text-label font-medium text-ink">{caption}</h4>
      <Table>
        <THead>
          <Tr>
            <Th>{t('adminApi.doc.params.name')}</Th>
            <Th>{t('adminApi.doc.params.detail')}</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.map((row) => (
            <Tr key={row.name}>
              <Td label={t('adminApi.doc.params.name')} mono heading>
                {row.name}
              </Td>
              <Td label={t('adminApi.doc.params.detail')} stack>
                {t(row.detailKey)}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}
